/**
 * Roads and railways.
 *
 * Roads: a minimum spanning tree over all cities (by overland distance) guarantees one connected
 * network; each city is then also linked to its nearest neighbours when the existing network would
 * force a long detour, which adds redundancy without parallel roads. Every link is an A* search over
 * terrain costs (mountains and marsh avoided, bridges expensive) in which existing road is cheap, so
 * later roads merge into earlier ones and form trunk routes. Short links are built first.
 *
 * Rails: the same scheme over capitals and cities of size ≥ 2, strongly preferring to run along
 * roads (and existing track), so the lines read as trunk railways following the main roads.
 */
import { type HexGrid, opposite } from '../core/hex';
import { type City, isWater, Terrain } from '../core/types';
import { isLand, type MapContext } from './context';
import { bfs, PathFinder, type StepCost } from './paths';

const TERRAIN_COST: Record<number, number> = {
  [Terrain.Plains]: 1,
  [Terrain.Urban]: 0.8,
  [Terrain.Forest]: 1.8,
  [Terrain.Hills]: 2.4,
  [Terrain.Marsh]: 5,
  [Terrain.Mountains]: 7,
};
/** Extra cost of bridging a river. */
const BRIDGE = 4;
/** Cost multiplier for reusing an existing road. */
const ROAD_REUSE = 0.3;
/** Build a redundant link when the network route is this much longer than the direct one. */
const DETOUR = 1.6;

export function buildRoads(
  ctx: MapContext,
  cities: readonly City[],
): { roads: Uint8Array; rails: Uint8Array } {
  const { grid, n, terrain, riverEdges } = ctx;
  const land = new Uint8Array(n);
  for (let h = 0; h < n; h++) land[h] = isLand(terrain[h]) ? 1 : 0;
  const finder = new PathFinder(grid);
  const roads = new Uint8Array(n);
  const rails = new Uint8Array(n);

  const roadCost: StepCost = (from, to, dir) => {
    if (isWater(terrain[to])) return Infinity;
    const base = TERRAIN_COST[terrain[to]] + (riverEdges[from] & (1 << dir) ? BRIDGE : 0);
    return roads[from] & (1 << dir) ? base * ROAD_REUSE : base;
  };
  const railCost: StepCost = (from, to, dir) => {
    if (isWater(terrain[to])) return Infinity;
    const bit = 1 << dir;
    if (rails[from] & bit) return 0.25;
    const river = riverEdges[from] & bit;
    if (roads[from] & bit) return 1 + (river ? 1 : 0);
    return 2.5 * TERRAIN_COST[terrain[to]] + (river ? BRIDGE : 0);
  };

  const hexes = cities.map((c) => c.hex);
  const dist = hexes.map((h) => bfs(grid, [h], land).dist);
  link(grid, finder, roads, hexes, dist, roadCost, 0.8 * ROAD_REUSE, 3);

  const trunk = cities.filter((c) => c.size >= 2).map((c) => c.hex);
  const trunkDist = trunk.map((h) => dist[hexes.indexOf(h)]);
  link(grid, finder, rails, trunk, trunkDist, railCost, 0.25, 2);
  return { roads, rails };
}

/** MST + nearest-neighbour links over `nodes`, marking paths in `net`. */
function link(
  grid: HexGrid,
  pf: PathFinder,
  net: Uint8Array,
  nodes: readonly number[],
  d: readonly Int32Array[],
  cost: StepCost,
  minStep: number,
  neighbours: number,
): void {
  const k = nodes.length;
  if (k < 2) return;
  // Prim's MST on overland step distance.
  const inTree = new Uint8Array(k);
  const bestD = new Float64Array(k).fill(Infinity);
  const bestFrom = new Int32Array(k).fill(-1);
  const edges: [number, number, number][] = [];
  inTree[0] = 1;
  for (let j = 1; j < k; j++) {
    bestD[j] = d[0][nodes[j]];
    bestFrom[j] = 0;
  }
  for (let added = 1; added < k; added++) {
    let next = -1;
    for (let j = 0; j < k; j++) if (!inTree[j] && (next < 0 || bestD[j] < bestD[next])) next = j;
    inTree[next] = 1;
    edges.push([bestFrom[next], next, bestD[next]]);
    for (let j = 0; j < k; j++) {
      const dj = d[next][nodes[j]];
      if (!inTree[j] && dj < bestD[j]) {
        bestD[j] = dj;
        bestFrom[j] = next;
      }
    }
  }
  edges.sort((a, b) => a[2] - b[2] || a[0] - b[0] || a[1] - b[1]);
  for (const [a, b] of edges) mark(grid, net, pf.find(nodes[a], nodes[b], cost, minStep));

  // Redundancy: nearest neighbours whose network route is a long detour.
  const extra: [number, number, number][] = [];
  for (let a = 0; a < k; a++) {
    const near = [...nodes.keys()]
      .filter((b) => b !== a)
      .sort((x, y) => d[a][nodes[x]] - d[a][nodes[y]] || x - y)
      .slice(0, neighbours);
    for (const b of near) extra.push([Math.min(a, b), Math.max(a, b), d[a][nodes[b]]]);
  }
  extra.sort((x, y) => x[2] - y[2] || x[0] - y[0] || x[1] - y[1]);
  const seen = new Set<number>();
  for (const [a, b, direct] of extra) {
    const key = a * k + b;
    if (seen.has(key)) continue;
    seen.add(key);
    const onNet = networkDistance(grid, net, nodes[a], nodes[b]);
    if (onNet > DETOUR * direct) mark(grid, net, pf.find(nodes[a], nodes[b], cost, minStep));
  }
}

/** Steps between two hexes along `net` (Infinity if not connected). */
function networkDistance(grid: HexGrid, net: Uint8Array, from: number, to: number): number {
  const seen = new Map<number, number>([[from, 0]]);
  const queue = [from];
  for (let i = 0; i < queue.length; i++) {
    const h = queue[i];
    const dh = seen.get(h)!;
    if (h === to) return dh;
    for (let dir = 0; dir < 6; dir++) {
      if (!(net[h] & (1 << dir))) continue;
      const m = grid.neighbour(h, dir);
      if (!seen.has(m)) {
        seen.set(m, dh + 1);
        queue.push(m);
      }
    }
  }
  return Infinity;
}

/** Sets the symmetric bits along a hex path. */
function mark(grid: HexGrid, net: Uint8Array, path: number[] | null): void {
  if (!path) return;
  for (let i = 1; i < path.length; i++) {
    const dir = grid.dirTo(path[i - 1], path[i]);
    net[path[i - 1]] |= 1 << dir;
    net[path[i]] |= 1 << opposite(dir);
  }
}
